import { Button, Heading, List, Span } from "@icg360/design-system";

import { Stack } from "components/common/stack";
import {
  type ThumbtackModalOpenTracking,
  useThumbtackModalContext,
} from "components/thumbtack-modal";
import { type KeystoneReferral } from "data/keystone-api-mapping";
import { useSelectedPolicy } from "data/ss-data-hooks";
import { capitalize, checkForNaStringValue, trackEvent } from "utils";

import styles from "./write-out-entry.module.scss";
import { WriteOutIcon } from "./write-out-icon";

export const WriteOutEntry = ({
  referral,
  showGallery,
  roofMarketplaceEligible,
  roofMarketplaceDrawerAvailable,
  onOpenRoofMarketplace,
}: {
  referral: KeystoneReferral;
  showGallery: () => void;
  roofMarketplaceEligible?: boolean;
  roofMarketplaceDrawerAvailable?: boolean;
  onOpenRoofMarketplace?: () => void | Promise<void>;
}) => {
  const { data } = useSelectedPolicy();
  const { openThumbtackModal } = useThumbtackModalContext();
  const thumbtackTracking = (): ThumbtackModalOpenTracking => ({
    referralId: referral.id ?? "",
    category: referral.category,
    searchTerm: referral.searchTerm,
  });
  const {
    actionStatement,
    contractor,
    category,
    groupedIssueStatements,
    groupedReasonings,
    inspectionReviewAssetIds,
    issueStatement,
    locationDetail,
    reasoning,
    searchTerm,
  } = referral;

  let writeOutHeading = checkForNaStringValue(issueStatement ?? "");
  if (groupedIssueStatements?.length) {
    writeOutHeading = "Roof - Multiple issues";
  }
  const reasoningDisplay = checkForNaStringValue(reasoning ?? "");
  let reasonings: string[] = [];
  if (groupedReasonings?.length) {
    reasonings = groupedReasonings;
  } else if (reasoningDisplay) {
    reasonings = [reasoningDisplay];
  }
  const locationDisplay = checkForNaStringValue(locationDetail ?? "");
  const actionStatementDisplay = checkForNaStringValue(actionStatement ?? "");
  const validSearchTerm = checkForNaStringValue(searchTerm ?? "");
  const contractorDisplay = checkForNaStringValue(
    contractor ?? "",
  )?.toLowerCase();
  const findContractorLabel = contractorDisplay
    ? `Find a ${contractorDisplay}`
    : "Find a qualified contractor.";

  return (
    <div className={styles.writeOut}>
      <div className={styles.writeOutIcon}>
        <WriteOutIcon category={category} />
      </div>
      <Stack gap="sm">
        {writeOutHeading ? (
          <Heading size="sm">{writeOutHeading}</Heading>
        ) : null}

        {reasonings.map((reasoning, idx) => (
          <Span key={idx} color="quiet" size="sm">
            {reasoning}
          </Span>
        ))}

        <div className={styles.twoEntryLine}>
          {locationDisplay ? (
            <div>
              <Span bold>Location: </Span>
              <Span>{locationDisplay}</Span>
            </div>
          ) : null}

          {inspectionReviewAssetIds?.length ? (
            <Span bold>
              <Button
                size="xs"
                leadingIcon="ImagePlaceholder"
                appearance="link"
                data-testid="write-out-photos-button"
                onPress={() => {
                  trackEvent("Inspection write-outs: Photo gallery opened", {
                    numberOfPhotos: inspectionReviewAssetIds.length,
                    referral,
                  });
                  showGallery();
                }}
              >
                {inspectionReviewAssetIds.length} photo
                {inspectionReviewAssetIds.length > 1 ? "s" : ""}
              </Button>
            </Span>
          ) : null}
        </div>

        {actionStatementDisplay ? (
          <div className={styles.resolveBox}>
            <Span bold>How to resolve:</Span>
            <List type="ul">
              <li className={styles.listItem}>{actionStatementDisplay}</li>
            </List>

            {(category ?? "").toLowerCase() === "roof" &&
            onOpenRoofMarketplace &&
            (roofMarketplaceEligible || validSearchTerm) ? (
              <Span bold>
                <Button
                  appearance="link"
                  onPress={() => {
                    if (roofMarketplaceEligible) {
                      void onOpenRoofMarketplace?.();
                      return;
                    }
                    if (validSearchTerm) {
                      trackEvent("write-out-contractor button pressed", {
                        contractorSearchTerm: validSearchTerm,
                        writeOutHeading: capitalize(writeOutHeading ?? ""),
                        subHeading: locationDisplay,
                      });
                      trackEvent(
                        "Inspection write-out: Contractor help clicked",
                        {
                          source: "thumbtack",
                          referralId: referral.id ?? "",
                          category: referral.category,
                        },
                      );
                      openThumbtackModal(
                        data?.propertyAddress?.zip ?? "",
                        validSearchTerm,
                        undefined,
                        thumbtackTracking(),
                      );
                    }
                  }}
                  data-testid={
                    roofMarketplaceEligible && roofMarketplaceDrawerAvailable
                      ? "write-out-roof-marketplace-or-contractor-button"
                      : "write-out-contractor-button"
                  }
                  {...(validSearchTerm
                    ? { "data-contractor-search-term": validSearchTerm }
                    : {})}
                >
                  {findContractorLabel}
                </Button>
              </Span>
            ) : contractorDisplay && validSearchTerm ? (
              <Span bold>
                <Button
                  appearance="link"
                  onPress={() => {
                    trackEvent(
                      "Inspection write-out: Contractor help clicked",
                      {
                        source: "thumbtack",
                        referralId: referral.id ?? "",
                        category: referral.category,
                      },
                    );
                    openThumbtackModal(
                      data?.propertyAddress?.zip ?? "",
                      validSearchTerm,
                      undefined,
                      thumbtackTracking(),
                    );
                  }}
                  data-testid="write-out-contractor-button"
                  data-contractor-search-term={validSearchTerm}
                >
                  {findContractorLabel}
                </Button>
              </Span>
            ) : null}
          </div>
        ) : null}
      </Stack>
    </div>
  );
};
